import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../../lib/cn.ts';

export interface ComboboxOption {
  id: string;
  label: string;
  /** Secondary line (e.g. the municipality under the district). */
  detail?: string;
}

/**
 * Text box with a list of suggestions (WAI-ARIA combobox pattern): arrows move, Enter
 * chooses, Escape closes. The options come from the caller (usually an API search on
 * `query`), so it works for districts, contacts or anything else.
 */
export function Combobox({
  id,
  query,
  onQueryChange,
  options,
  onSelect,
  placeholder,
  loading = false,
  empty = 'Sin resultados',
  describedBy,
  invalid = false,
  footer,
  ariaLabel,
}: {
  id?: string;
  /** Accessible name when there is no visible <label> (filters). */
  ariaLabel?: string;
  query: string;
  onQueryChange: (value: string) => void;
  options: ComboboxOption[];
  onSelect: (option: ComboboxOption) => void;
  placeholder?: string;
  loading?: boolean;
  empty?: string;
  describedBy?: string | undefined;
  invalid?: boolean;
  footer?: ReactNode;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const visible = open && query.trim().length > 0;
  const current = Math.min(active, Math.max(options.length - 1, 0));

  const choose = (option: ComboboxOption | undefined) => {
    if (!option) return;
    onSelect(option);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && visible) {
      event.preventDefault();
      choose(options[current]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={listId}
        aria-activedescendant={
          visible && options[current] ? `${listId}-${String(current)}` : undefined
        }
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        placeholder={placeholder}
        value={query}
        onChange={(event) => {
          onQueryChange(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          // Let a click on an option land first.
          setTimeout(() => {
            setOpen(false);
          }, 150);
        }}
        onKeyDown={onKeyDown}
        className="min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-base text-foreground placeholder:text-muted-foreground aria-[invalid=true]:border-danger sm:text-sm"
      />
      {visible && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {loading && options.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">Buscando…</li>
          )}
          {!loading && options.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">{empty}</li>
          )}
          {options.map((option, index) => (
            <li
              key={option.id}
              id={`${listId}-${String(index)}`}
              role="option"
              aria-selected={index === current}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => {
                setActive(index);
              }}
              className={cn(
                'cursor-pointer rounded-md px-3 py-2 text-sm',
                index === current && 'bg-soft text-soft-foreground',
              )}
            >
              <span className="block font-medium">{option.label}</span>
              {option.detail && (
                <span className="block text-xs text-muted-foreground">{option.detail}</span>
              )}
            </li>
          ))}
          {footer}
        </ul>
      )}
    </div>
  );
}
